/**
 * Web Speech dictation for the widget composer.
 *
 * Lifted from SearchSidePanel's `handleDictateToggle` (continuous append, with
 * an explicit getUserMedia pre-flight so the browser actually shows the mic
 * prompt instead of failing silently). The one behavioural change: stopping
 * dictation does NOT auto-send. The panel used to route dictated text through
 * the voice intent pipeline, which the widget deliberately does not have — so
 * the transcript lands in the composer and the user presses send.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface DictationController {
  isDictating: boolean;
  /** False when the browser has no SpeechRecognition at all — hide the mic. */
  isSupported: boolean;
  toggle: () => void;
  stop: () => void;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function getSpeechRecognition(): any {
  if (typeof window === 'undefined') return undefined;
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
}

/**
 * @param onTranscript - called with each finalised phrase; the composer appends
 *   it to whatever the user has already typed.
 * @param lang - BCP-47 tag, defaults to the browser's `en-US`.
 */
export function useDictation(
  onTranscript: (text: string) => void,
  lang = 'en-US',
): DictationController {
  const [isDictating, setIsDictating] = useState(false);
  const recognitionRef = useRef<any>(null);
  // Read through a ref so a re-created callback does not tear down an
  // in-flight recognition session.
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const isSupported = typeof window !== 'undefined' && !!getSpeechRecognition();

  const stop = useCallback(() => {
    recognitionRef.current?.stop?.();
    recognitionRef.current = null;
    setIsDictating(false);
  }, []);

  const toggle = useCallback(() => {
    if (recognitionRef.current) {
      stop();
      return;
    }
    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition) return;

    // Pre-flight the permission explicitly. Safari in particular will start
    // and immediately end recognition with no prompt otherwise.
    navigator.mediaDevices
      ?.getUserMedia({ audio: true })
      .then((stream) => {
        stream.getTracks().forEach((t) => t.stop());
        const recognition = new SpeechRecognition();
        recognition.lang = lang;
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.onresult = (event: any) => {
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const result = event.results[i];
            if (!result.isFinal) continue;
            const text = String(result[0]?.transcript ?? '').trim();
            if (text) onTranscriptRef.current(text);
          }
        };
        recognition.onerror = () => {
          recognitionRef.current = null;
          setIsDictating(false);
        };
        recognition.onend = () => {
          recognitionRef.current = null;
          setIsDictating(false);
        };
        recognitionRef.current = recognition;
        recognition.start();
        setIsDictating(true);
      })
      .catch(() => {
        // Permission denied — leave the button in its resting state.
        setIsDictating(false);
      });
  }, [lang, stop]);

  // Closing the widget mid-dictation must release the microphone.
  useEffect(() => () => recognitionRef.current?.stop?.(), []);

  return { isDictating, isSupported, toggle, stop };
}
